import { Flex, Skeleton, Spacer, Stack } from "@chakra-ui/react";
import {
  Button,
  getDocumentsRoute,
  getOpportunityDetailsRoute,
  getTransactionsRoute,
} from "@verivest/shared-components";
import { useVeriToast } from "@verivest/shared-components/";
import { ManagerProfileSectionContainer } from "@verivest/shared-components/src/components/manager-profile/ManagerProfileSectionContainer";
import { ManagerContactModal } from "@verivest/shared-components/src/components/manager-profile/modals/ManagerContactModal";
import { OpportunityCover } from "@verivest/shared-components/src/components/manager-profile/OpportunityCover";
import { VeriAsyncRouteComponentType } from "@verivest/shared-components/src/defines/after-js-types";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import useSWR from "swr";
import DistributionPreferencesDrawer from "../../../components/modals/DistributionPreferencesDrawer";
import { ManagerWhiteLabelRouteParams } from "../../../routes/investorPortalPages.types";
import { removeTSRequiredPropsWarn } from "../../../utils/removeTSRequiredPropsWarn";
import { InvestmentDetailsHeading } from "./investment-details/InvestmentDetailsHeading";
import { InvestmentDetailsHoldingDetails } from "./investment-details/InvestmentDetailsHoldingDetails";
import { InvestmentOverviewDetails } from "./investment-details/InvestmentOverviewDetails";
import {
  ManagerWhiteLabelRoutesServerInitialProps,
  WhiteLabelPageExtraProps,
} from "./types";
const InvestmentDetails: VeriAsyncRouteComponentType<
  ManagerWhiteLabelRoutesServerInitialProps & WhiteLabelPageExtraProps,
  ManagerWhiteLabelRouteParams
> = (props) => {
  const { toastSuccess } = useVeriToast();

  const { subSection: accountId, subSectionId: offeringId } =
    props.match.params;
  const [editModalOpen, setEditModalOpen] = React.useState(false);

  const { data, isValidating, mutate } = useSWR(
    `/grouped-investments/${accountId}/${offeringId}`,
    {
      focusThrottleInterval: 0,
    },
  );

  const { data: groupedInvestment } = data || {};

  const { data: rawOpportunity } = useSWR(
    groupedInvestment?.offering?.opportunity?.id
      ? `/opportunities/${groupedInvestment?.offering?.opportunity?.id}`
      : undefined,
    {
      focusThrottleInterval: 0,
    },
  );

  const accountRequest = useSWR(
    groupedInvestment?.account?.id &&
      `/accounts/${groupedInvestment.account.id}`,
    { focusThrottleInterval: 0 },
  );
  const { data: account } = accountRequest?.data || {};
  if (groupedInvestment && !groupedInvestment.id && !isValidating) return <></>;

  if (!groupedInvestment || !account)
    return (
      <ManagerProfileSectionContainer noBorder>
        <Stack>
          <Skeleton height="30px" maxWidth="60px" />
          <Skeleton height="20px" />
          <Skeleton height="20px" />
        </Stack>
      </ManagerProfileSectionContainer>
    );

  const opportunity =
    rawOpportunity?.data || groupedInvestment?.offering?.opportunity;

  if (!props.manager) return <></>;

  return (
    <ManagerProfileSectionContainer noBorder>
      <OpportunityCover
        opportunity={opportunity}
        manager={props.manager}
        mb={6}
        mt={{
          base: 4,
          md: 8,
        }}
      />

      {groupedInvestment.opportunity && (
        <OpportunityCover
          opportunity={groupedInvestment.opportunity}
          manager={props.manager}
          mb={6}
        />
      )}
      <Flex className={"flex-col sm:flex-row gap-3 mb-10"}>
        <InvestmentDetailsHeading>View investment:</InvestmentDetailsHeading>
        <Button
          as={RouterLink}
          to={getDocumentsRoute(
            opportunity.id,
            opportunity.name,
            groupedInvestment.account.id,
            groupedInvestment.account.nameOfRecord,
            groupedInvestment.account.mnumber,
            groupedInvestment.account.accountType,
          )}
        >
          Documents
        </Button>
        <Button
          as={RouterLink}
          to={getTransactionsRoute(
            opportunity.id,
            opportunity.name,
            groupedInvestment.account.id,
            groupedInvestment.account.nameOfRecord,
            groupedInvestment.account.mnumber,
            groupedInvestment.account.accountType,
          )}
        >
          Transactions
        </Button>
        <Spacer />
        <Button
          outline
          as={RouterLink}
          to={getOpportunityDetailsRoute(opportunity.id)}
        >
          Opportunity details
        </Button>
        {opportunity.manager.managerContact && (
          <ManagerContactModal
            justAuthenticated
            managerId={opportunity.managerId}
            profile={
              groupedInvestment?.offering?.opportunity.manager.managerContact
            }
            subject={"Inquiry about my investment"}
            message={`Hello, I have a question about my investment ${
              opportunity?.name ? " " + opportunity?.name : ""
            }.`}
          >
            <Button h={"38px"} outline>
              Contact manager
            </Button>
          </ManagerContactModal>
        )}
      </Flex>
      <InvestmentOverviewDetails
        openDistributionPreference={() => setEditModalOpen(true)}
        groupedInvestment={groupedInvestment}
      />
      {!!accountId && !!offeringId && (
        <InvestmentDetailsHoldingDetails
          accountId={accountId}
          offeringId={offeringId}
        />
      )}
      {editModalOpen && (
        <DistributionPreferencesDrawer
          investment={{
            account,
            offering: groupedInvestment.offering,
            identity: account.identity,
          }}
          distributionPreference={groupedInvestment.distributionPreference}
          onFinish={() => {
            mutate();
            setEditModalOpen(false);

            toastSuccess("Your distribution preference has been updated.");
          }}
          onClose={() => setEditModalOpen(false)}
          /* Shows address disclaimer for sc-23372 */
          // can be removed after sc-23372 is no longer relevant
          // or when DistributionPreferencesDrawer turns TS
          showAddressDisclaimer={true}
          {...removeTSRequiredPropsWarn}
        />
      )}
    </ManagerProfileSectionContainer>
  );
};

InvestmentDetails.getChunkName = () =>
  "investor-portal-3-white-label-investment-details";

export default InvestmentDetails;
